import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, CheckCircle2, AlertTriangle, RefreshCw, Building2, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const FAVCY_ORANGE = "#FF5C00";

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("admin_token") || "";
  return { "Content-Type": "application/json", "x-admin-token": token };
}

function fmtINR(n: number): string {
  if (!n || n <= 0) return "₹0";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)} K`;
  return `₹${n.toLocaleString("en-IN")}`;
}

type Company = { id: number; name: string; sector: string; cohortId: number | null; isOpenForSecondary: boolean; openedAt: string | null };
type Investor = { id: number; name: string; email: string; firm: string | null };
type AllocationLine = { id?: number; companyId: number; companyName?: string; amountInr: number };
type Allocation = {
  id: number;
  investorId: number;
  investorName: string;
  investorEmail: string;
  totalAmountInr: number;
  status: "draft" | "approved";
  notes: string | null;
  createdByAdminEmail: string;
  approvedByAdminEmail: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lines: Array<AllocationLine & { id: number; companyName: string }>;
  linesSumInr: number;
};

export default function AdminSecondary() {
  const { toast } = useToast();
  const [tab, setTab] = useState<"offerings" | "allocations">("offerings");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editAlloc, setEditAlloc] = useState<Allocation | null>(null);

  async function loadAll() {
    setLoading(true);
    try {
      const [cRes, iRes, aRes] = await Promise.all([
        fetch("/api/admin/secondary/companies", { headers: authHeaders() }),
        fetch("/api/admin/secondary/investors", { headers: authHeaders() }),
        fetch("/api/admin/secondary/allocations", { headers: authHeaders() }),
      ]);
      if (!cRes.ok || !iRes.ok || !aRes.ok) throw new Error("Failed to load");
      const cJson = await cRes.json();
      const iJson = await iRes.json();
      const aJson = await aRes.json();
      setCompanies(cJson.companies || []);
      setInvestors(iJson.investors || []);
      setAllocations(aJson.allocations || []);
    } catch (e: any) {
      toast({ title: "Load failed", description: e?.message || "Could not load data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  async function toggleCompany(c: Company) {
    const next = !c.isOpenForSecondary;
    try {
      const r = await fetch(`/api/admin/secondary/companies/${c.id}/toggle`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ open: next }),
      });
      if (!r.ok) throw new Error((await r.json()).error || "Toggle failed");
      setCompanies((prev) => prev.map((x) => x.id === c.id ? { ...x, isOpenForSecondary: next } : x));
      toast({ title: next ? "Opened for secondary" : "Closed", description: c.name });
    } catch (e: any) {
      toast({ title: "Toggle failed", description: e?.message, variant: "destructive" });
    }
  }

  const openCompanies = useMemo(() => companies.filter((c) => c.isOpenForSecondary), [companies]);

  return (
    <div className="space-y-6" data-testid="page-admin-secondary">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif font-bold tracking-tight">Secondary Purchase Basket</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Flag companies open for secondary, then allocate investor cheques across the basket.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadAll} disabled={loading} data-testid="button-refresh">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList>
          <TabsTrigger value="offerings" data-testid="tab-offerings">
            <Building2 className="w-4 h-4 mr-2" /> Companies ({openCompanies.length} open)
          </TabsTrigger>
          <TabsTrigger value="allocations" data-testid="tab-allocations">
            <Users className="w-4 h-4 mr-2" /> Investor Allocations ({allocations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="offerings" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Open-for-secondary toggle</CardTitle>
              <p className="text-xs text-muted-foreground">
                Only companies marked open here can appear in an investor's allocation basket.
              </p>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
              ) : companies.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No companies yet.</p>
              ) : (
                <div className="divide-y">
                  {companies.map((c) => (
                    <div key={c.id} className="flex items-center justify-between py-3" data-testid={`row-company-${c.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{c.name}</div>
                        <div className="text-xs text-muted-foreground">{c.sector}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        {c.isOpenForSecondary && (
                          <Badge style={{ backgroundColor: FAVCY_ORANGE, color: "white" }} className="border-none">OPEN</Badge>
                        )}
                        <Switch
                          checked={c.isOpenForSecondary}
                          onCheckedChange={() => toggleCompany(c)}
                          data-testid={`switch-company-${c.id}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="allocations" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => setCreateOpen(true)}
              style={{ backgroundColor: FAVCY_ORANGE }}
              className="text-white hover:opacity-90"
              data-testid="button-new-allocation"
            >
              <Plus className="w-4 h-4 mr-2" /> New allocation
            </Button>
          </div>

          {loading ? (
            <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : allocations.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
              No allocations yet. Click "New allocation" to create one.
            </CardContent></Card>
          ) : (
            <div className="grid gap-3">
              {allocations.map((a) => (
                <AllocationCard
                  key={a.id}
                  allocation={a}
                  onEdit={() => setEditAlloc(a)}
                  onChanged={loadAll}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <CreateAllocationDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        investors={investors}
        onCreated={async (newId) => {
          setCreateOpen(false);
          await loadAll();
          // Open editor for the freshly-created allocation
          const fresh = await fetch("/api/admin/secondary/allocations", { headers: authHeaders() })
            .then((r) => r.json())
            .catch(() => ({ allocations: [] }));
          const found = (fresh.allocations || []).find((x: Allocation) => x.id === newId);
          if (found) setEditAlloc(found);
        }}
      />

      {editAlloc && (
        <EditLinesDialog
          allocation={editAlloc}
          openCompanies={openCompanies}
          onClose={() => setEditAlloc(null)}
          onSaved={async () => { setEditAlloc(null); await loadAll(); }}
        />
      )}
    </div>
  );
}

function AllocationCard({ allocation, onEdit, onChanged }: { allocation: Allocation; onEdit: () => void; onChanged: () => void }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const isApproved = allocation.status === "approved";
  const sumMatches = allocation.linesSumInr === allocation.totalAmountInr;
  const remaining = allocation.totalAmountInr - allocation.linesSumInr;

  async function approve() {
    setBusy(true);
    try {
      const r = await fetch(`/api/admin/secondary/allocations/${allocation.id}/approve`, {
        method: "POST",
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error((await r.json()).error || "Approve failed");
      toast({ title: "Approved", description: `${allocation.investorName}'s allocation is now visible on their dashboard.` });
      onChanged();
    } catch (e: any) {
      toast({ title: "Approve failed", description: e?.message, variant: "destructive" });
    } finally { setBusy(false); }
  }

  async function del() {
    if (!confirm(`Delete draft allocation for ${allocation.investorName}?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/admin/secondary/allocations/${allocation.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!r.ok && r.status !== 204) throw new Error((await r.json()).error || "Delete failed");
      toast({ title: "Deleted" });
      onChanged();
    } catch (e: any) {
      toast({ title: "Delete failed", description: e?.message, variant: "destructive" });
    } finally { setBusy(false); }
  }

  return (
    <Card data-testid={`card-allocation-${allocation.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium truncate">{allocation.investorName}</span>
              <span className="text-xs text-muted-foreground truncate">{allocation.investorEmail}</span>
              {isApproved ? (
                <Badge style={{ backgroundColor: FAVCY_ORANGE, color: "white" }} className="border-none">APPROVED</Badge>
              ) : (
                <Badge variant="outline">DRAFT</Badge>
              )}
            </div>
            <div className="mt-2 flex items-baseline gap-3 flex-wrap">
              <div className="text-lg font-serif font-bold">{fmtINR(allocation.totalAmountInr)}</div>
              <div className="text-xs text-muted-foreground">
                {allocation.lines.length} {allocation.lines.length === 1 ? "company" : "companies"} · lines sum {fmtINR(allocation.linesSumInr)}
              </div>
              {!sumMatches && !isApproved && (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {remaining > 0 ? `${fmtINR(remaining)} unallocated` : `Over by ${fmtINR(-remaining)}`}
                </span>
              )}
            </div>
            {allocation.notes && <p className="text-xs text-muted-foreground mt-1">{allocation.notes}</p>}
            {allocation.lines.length > 0 && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                {allocation.lines.map((l) => (
                  <div key={l.id} className="flex justify-between border rounded px-2 py-1">
                    <span className="truncate">{l.companyName}</span>
                    <span className="font-mono ml-2">{fmtINR(l.amountInr)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {!isApproved && (
              <>
                <Button size="sm" variant="outline" onClick={onEdit} data-testid={`button-edit-${allocation.id}`}>Edit lines</Button>
                <Button
                  size="sm"
                  onClick={approve}
                  disabled={busy || !sumMatches || allocation.lines.length === 0}
                  style={{ backgroundColor: sumMatches && allocation.lines.length > 0 ? FAVCY_ORANGE : undefined }}
                  className="text-white hover:opacity-90"
                  data-testid={`button-approve-${allocation.id}`}
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                  Approve
                </Button>
                <Button size="sm" variant="ghost" onClick={del} disabled={busy} data-testid={`button-delete-${allocation.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CreateAllocationDialog({
  open, onClose, investors, onCreated,
}: {
  open: boolean; onClose: () => void; investors: Investor[];
  onCreated: (id: number) => void;
}) {
  const { toast } = useToast();
  const [investorId, setInvestorId] = useState<string>("");
  const [totalLakh, setTotalLakh] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setInvestorId(""); setTotalLakh(""); setNotes(""); }
  }, [open]);

  async function submit() {
    const inr = Math.round(Number(totalLakh) * 100000);
    if (!investorId || !Number.isFinite(inr) || inr <= 0) {
      toast({ title: "Invalid input", description: "Pick an investor and enter a positive amount.", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const r = await fetch("/api/admin/secondary/allocations", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ investorId: Number(investorId), totalAmountInr: inr, notes: notes || null }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Create failed");
      toast({ title: "Allocation created", description: "Now distribute the amount across companies." });
      onCreated(j.id);
    } catch (e: any) {
      toast({ title: "Create failed", description: e?.message, variant: "destructive" });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>New secondary allocation</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Investor</Label>
            <Select value={investorId} onValueChange={setInvestorId}>
              <SelectTrigger data-testid="select-investor"><SelectValue placeholder="Select an investor" /></SelectTrigger>
              <SelectContent>
                {investors.map((i) => (
                  <SelectItem key={i.id} value={String(i.id)}>
                    {i.name} {i.firm ? `· ${i.firm}` : ""} · {i.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Total cheque size (₹ lakh)</Label>
            <Input
              type="number"
              min="0"
              step="0.5"
              value={totalLakh}
              onChange={(e) => setTotalLakh(e.target.value)}
              placeholder="e.g. 12 for ₹12 lakh"
              data-testid="input-total-lakh"
            />
            {totalLakh && Number(totalLakh) > 0 && (
              <p className="text-xs text-muted-foreground mt-1">= {fmtINR(Math.round(Number(totalLakh) * 100000))}</p>
            )}
          </div>
          <div>
            <Label>Notes (admin-only)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="input-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            style={{ backgroundColor: FAVCY_ORANGE }}
            className="text-white hover:opacity-90"
            data-testid="button-create-submit"
          >
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditLinesDialog({
  allocation, openCompanies, onClose, onSaved,
}: {
  allocation: Allocation; openCompanies: Company[]; onClose: () => void; onSaved: () => void;
}) {
  const { toast } = useToast();
  const [lines, setLines] = useState<Record<number, number>>(() => {
    const m: Record<number, number> = {};
    for (const l of allocation.lines) m[l.companyId] = l.amountInr;
    return m;
  });
  const [busy, setBusy] = useState(false);

  const total = allocation.totalAmountInr;
  const sum = Object.values(lines).reduce((s, v) => s + (Number(v) || 0), 0);
  const remaining = total - sum;
  const matches = sum === total;

  function applyEqualSplit() {
    if (openCompanies.length === 0) return;
    const per = Math.floor(total / openCompanies.length);
    const remainder = total - per * openCompanies.length;
    const next: Record<number, number> = {};
    openCompanies.forEach((c, idx) => {
      next[c.id] = per + (idx === 0 ? remainder : 0);
    });
    setLines(next);
  }

  function setLine(companyId: number, val: string) {
    const n = Math.round(Number(val) * 100000); // input is in lakh
    setLines((prev) => ({ ...prev, [companyId]: Number.isFinite(n) && n >= 0 ? n : 0 }));
  }

  async function save() {
    setBusy(true);
    try {
      const payload = {
        lines: Object.entries(lines)
          .filter(([, v]) => Number(v) > 0)
          .map(([companyId, amountInr]) => ({ companyId: Number(companyId), amountInr: Number(amountInr) })),
      };
      const r = await fetch(`/api/admin/secondary/allocations/${allocation.id}/lines`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error((await r.json()).error || "Save failed");
      toast({ title: "Lines saved" });
      onSaved();
    } catch (e: any) {
      toast({ title: "Save failed", description: e?.message, variant: "destructive" });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={!!allocation} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Distribute {fmtINR(total)} across companies</DialogTitle>
          <p className="text-xs text-muted-foreground">
            {allocation.investorName} · {allocation.investorEmail}
          </p>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg border bg-muted/30">
            <div className="text-sm">
              <span className="font-medium">{fmtINR(sum)}</span> of <span className="font-medium">{fmtINR(total)}</span> allocated
              {matches ? (
                <span className="ml-2 text-green-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> matches</span>
              ) : (
                <span className="ml-2 text-amber-600">
                  {remaining > 0 ? `${fmtINR(remaining)} remaining` : `over by ${fmtINR(-remaining)}`}
                </span>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={applyEqualSplit}
              disabled={openCompanies.length === 0}
              data-testid="button-equal-split"
            >
              Equal split across {openCompanies.length}
            </Button>
          </div>

          {openCompanies.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No companies are open for secondary. Open some on the Companies tab first.
            </p>
          ) : (
            <div className="space-y-2">
              {openCompanies.map((c) => {
                const valInr = lines[c.id] || 0;
                const valLakh = valInr ? (valInr / 100000).toString() : "";
                return (
                  <div key={c.id} className="flex items-center gap-3" data-testid={`line-company-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.sector}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={valLakh}
                        onChange={(e) => setLine(c.id, e.target.value)}
                        placeholder="₹ lakh"
                        className="w-28"
                        data-testid={`input-line-${c.id}`}
                      />
                      <span className="text-xs text-muted-foreground w-20">
                        {valInr > 0 ? fmtINR(valInr) : "—"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
          <Button
            onClick={save}
            disabled={busy}
            style={{ backgroundColor: FAVCY_ORANGE }}
            className="text-white hover:opacity-90"
            data-testid="button-save-lines"
          >
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save lines
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
